import type { MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import type { ReportsMatterSearchTableRow } from "@/lib/types";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";
import { getMatterSearchResultAssigneeDisplayNames } from "@/lib/utils/matters/matter-search-result-utils";

export const mapMyMattersSearchResultToTableRow = (
  matter: MatterSearchResult,
): ReportsMatterSearchTableRow => {
  const primaryStatute = resolveReportPrimaryStatute(
    (matter.statutesOfLimitations ?? []).map(({ description, ...statute }) => ({
      ...statute,
      description: description ?? undefined,
    })),
  );
  const clients = matter.clients ?? [];

  return {
    claimTypes: matter.claimTypes ?? [],
    relatedMatters: matter.relatedMatters,
    matterId: matter.id,
    dateCreated: matter.dateCreated,
    dateModified: matter.dateModified,
    matterTitle: matter.title,
    matterIncidentDescription: matter.incidentDescription,
    matterClientInjuryDescription: matter.clientInjuryDescription,
    matterStatusLabel: matter.status?.label,
    matterStatusCategory: matter.status?.category.label,
    matterDefendantName: matter.defendantNames,
    insuranceCarriers: matter.insuranceCarriers,
    claimNumber: matter.insuranceClaimNumbers,
    coverage: (matter.insuranceCoverages ?? []).map((coverage) => ({
      coverage: coverage.coverage,
      matterContactConnectionContactName: coverage.contactName,
      matterInsuranceId: coverage.insuranceId,
    })),
    liability: (matter.insuranceLiabilities ?? []).map((liability) => ({
      liability: liability.liability,
      liabilityPercentage:
        liability.liabilityPercentage == null
          ? null
          : Number(liability.liabilityPercentage),
      matterContactConnectionContactName: liability.contactName,
      matterInsuranceId: liability.insuranceId,
    })),
    policyNumber: matter.insurancePolicyNumbers,
    matterExpenseTotal:
      matter.expenseTotal == null ? null : Number(matter.expenseTotal),
    medicalTreatmentBillTotal:
      matter.medicalTreatmentBillTotal == null
        ? null
        : Number(matter.medicalTreatmentBillTotal),
    matterEmailAddress: matter.emailAddress,
    incidentTypeLabel: matter.incidentType?.label,
    matterDateOfLoss: matter.dateOfLoss,
    lastTreatment: matter.lastTreatment,
    nextTreatment: matter.nextTreatment,
    recordsPending: matter.recordsPending,
    daysInStatus: matter.daysInStatus,
    matterStatuteOfLimitations: primaryStatute.matterStatuteOfLimitations,
    matterStatuteOfLimitationsConfirmed:
      primaryStatute.matterStatuteOfLimitationsConfirmed,
    matterIncidentAddressCity: matter.incidentAddressCity,
    matterIncidentAddressState: matter.incidentAddressState,
    matterIncidentAddressStreet1: matter.incidentAddressStreet1 ?? "",
    matterIncidentAddressStreet2: matter.incidentAddressStreet2 ?? "",
    matterIncidentAddressZip: matter.incidentAddressZip ?? "",
    matterIncidentAddressCountry: matter.incidentAddressCountry ?? "",
    matterContactConnection: clients.map((client) => ({
      label: "Client",
      matterContactConnectionContactName: client.name ?? undefined,
      matterContactConnectionContactEmail: (client.emailAddresses ?? []).map(
        (address) => ({ address }),
      ),
      matterContactConnectionContactPhone: (client.phoneNumbers ?? []).map(
        (phone) =>
          typeof phone === "string"
            ? { number: phone }
            : { number: phone.number, extension: phone.extension },
      ),
    })),
    matterAssignee: getMatterSearchResultAssigneeDisplayNames(matter).map(
      (name) => {
        const [firstName, ...lastName] = name.split(" ");
        return { firstName, lastName: lastName.join(" ") };
      },
    ),
    matterTeamConnection: (matter.teams ?? []).map((label) => ({ label })),
    matterTagConnection: (matter.tags ?? []).map((label) => ({ label })),
  };
};
